/* eslint-disable sonarjs/no-duplicate-string */
import { DecimalPipe, formatCurrency } from '@angular/common';
import {
    type AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    computed,
    inject,
    input,
    LOCALE_ID,
    type OnChanges,
    reflectComponentType,
} from '@angular/core';
import { UiTableComponent } from '@big-direkt/ui/table';
import { EnvironmentService, ScrollService } from '@big-direkt/utils/environment';
import { TableRowModel, type TypedSimpleChanges } from '@big-direkt/utils/shared';
import { TranslocoDirective } from '@jsverse/transloco';
import { ContributionCalculatorFormResult } from '../models/contribution-calculator-result';
import { TaxonomyData } from '../models/insurant-calculator.model';

@Component({
    selector: 'big-service-tool-contribution-calculator-result',
    templateUrl: './contribution-calculator-result.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        '[class]': 'classList()',
    },
    imports: [DecimalPipe, TranslocoDirective, UiTableComponent],
})
export class ContributionCalculatorResultComponent implements OnChanges, AfterViewInit {
    private readonly cssClasses = [
        'border-r-0!',
        'md:border-r-1!',
        'last:border-b-1',
        'group-last/row:last:border-b-0',
        'md:border-b-0',
        'md:last:border-b-0',
        'mb-0!',
        'text-primary-800!',
        'dark:text-white!',
    ];

    private readonly cssClassDarkBg = [
        ...this.cssClasses,
        'bg-primary-100!',
        'dark:bg-primary-700!',
        'md:border-r-primary-50!',
        'dark:md:border-r-primary-600!',
        'border-b-primary-50',
        'dark:border-b-primary-600',
    ].join(' ');

    private readonly cssClassLightBg = [
        ...this.cssClasses,
        'bg-primary-50!',
        'dark:bg-primary-600!',
        'md:border-r-primary-100!',
        'dark:md:border-r-primary-700!',
        'border-b-primary-100',
        'dark:border-b-primary-700',
    ].join(' ');

    private readonly environmentService = inject(EnvironmentService);
    private readonly locale = inject(LOCALE_ID);
    private readonly scrollService = inject(ScrollService);

    public readonly classList = input('block');
    public readonly calculationResult = input.required<ContributionCalculatorFormResult>();
    public readonly servicePhoneNumber = this.environmentService.servicePhoneNumber;
    public readonly taxonomies = input.required<TaxonomyData>();

    public readonly monthlyAmountTableRows = computed<TableRowModel>(() => {
        const rows: TableRowModel = [];

        const { monthlyContribution, totalContributionEmployer, totalDeductible } = this.calculationResult();
        const { healthInsurance, nursingCareInsurance } = monthlyContribution;

        // healthInsurance
        rows.push([
            {
                type: 'string',
                cssClass: this.cssClassDarkBg,
                value: 'stContributionCalculator.results.healthInsuranceContributionRate',
            },
            {
                type: 'string-translated',
                cssClass: `${this.cssClassDarkBg} font-bold md:text-right`,
                value: formatCurrency(healthInsurance.total, this.locale, '€'),
            },
        ]);

        if (healthInsurance.employerShare) {
            rows.push([
                {
                    type: 'string',
                    cssClass: `${this.cssClassDarkBg} font-normal!`,
                    value: 'stContributionCalculator.results.employerContribution',
                },
                {
                    type: 'string-translated',
                    cssClass: `${this.cssClassDarkBg} md:text-right`,
                    value: formatCurrency(healthInsurance.employerShare, this.locale, '€'),
                },
            ]);
        }

        if (healthInsurance.pensionInsuranceShare) {
            rows.push([
                {
                    type: 'string',
                    cssClass: `${this.cssClassDarkBg} font-normal!`,
                    value: 'stContributionCalculator.results.pensionInsuranceCarrierContribution',
                },
                {
                    type: 'string-translated',
                    cssClass: `${this.cssClassDarkBg} md:text-right`,
                    value: formatCurrency(healthInsurance.pensionInsuranceShare, this.locale, '€'),
                },
            ]);
        }

        if (healthInsurance.deductible) {
            rows.push([
                {
                    type: 'string',
                    cssClass: `${this.cssClassDarkBg} font-normal!`,
                    value: 'stContributionCalculator.results.deductible',
                },
                {
                    type: 'string-translated',
                    cssClass: `${this.cssClassDarkBg} md:text-right`,
                    value: formatCurrency(healthInsurance.deductible, this.locale, '€'),
                },
            ]);
        }

        // nursingCareInsurance
        rows.push([
            {
                type: 'string',
                cssClass: this.cssClassLightBg,
                value: 'stContributionCalculator.results.nursingCareInsuranceRate',
            },
            {
                type: 'string-translated',
                cssClass: `${this.cssClassLightBg} font-bold md:text-right`,
                value: formatCurrency(nursingCareInsurance.total, this.locale, '€'),
            },
        ]);

        if (nursingCareInsurance.employerShare) {
            rows.push([
                {
                    type: 'string',
                    cssClass: `${this.cssClassLightBg} font-normal!`,
                    value: 'stContributionCalculator.results.employerContribution',
                },
                {
                    type: 'string-translated',
                    cssClass: `${this.cssClassLightBg} md:text-right`,
                    value: formatCurrency(nursingCareInsurance.employerShare, this.locale, '€'),
                },
            ]);
        }

        if (nursingCareInsurance.deductible) {
            rows.push([
                {
                    type: 'string',
                    cssClass: `${this.cssClassLightBg} font-normal!`,
                    value: 'stContributionCalculator.results.deductible',
                },
                {
                    type: 'string-translated',
                    cssClass: `${this.cssClassLightBg} md:text-right`,
                    value: formatCurrency(nursingCareInsurance.deductible, this.locale, '€'),
                },
            ]);
        }

        if (totalDeductible) {
            rows.push([
                {
                    type: 'string',
                    cssClass: this.cssClassDarkBg,
                    value: 'stContributionCalculator.results.monthlyDeductible',
                },
                {
                    type: 'string-translated',
                    cssClass: `${this.cssClassDarkBg} font-bold! md:text-right`,
                    value: formatCurrency(totalDeductible, this.locale, '€'),
                },
            ]);
        }

        if (totalContributionEmployer) {
            rows.push([
                {
                    type: 'string',
                    cssClass: this.cssClassLightBg,
                    value: 'stContributionCalculator.results.monthlyEmployerContribution',
                },
                {
                    type: 'string-translated',
                    cssClass: `${this.cssClassLightBg} font-bold! md:text-right`,
                    value: formatCurrency(totalContributionEmployer, this.locale, '€'),
                },
            ]);
        }

        return rows;
    });

    public readonly calculationBasisTableRows = computed<TableRowModel>(() => {
        const rows: TableRowModel = [];

        const { canClaimSickPay, showContributionAssessmentLimit, contributionAssessmentLimit, showMinimumAssessmentBasis, minimumAssessmentBasis } =
            this.calculationResult();

        rows.push([
            {
                type: 'string',
                cssClass: this.cssClassDarkBg,
                value: 'stContributionCalculator.results.sicknessBenefitContribution',
            },
            {
                type: 'string',
                cssClass: `${this.cssClassDarkBg} font-bold! md:text-right`,
                value: canClaimSickPay ? 'stContributionCalculator.results.yes' : 'stContributionCalculator.results.no',
            },
        ]);

        if (showContributionAssessmentLimit) {
            rows.push([
                {
                    type: 'string',
                    cssClass: this.cssClassLightBg,
                    value: 'stContributionCalculator.results.contributionAssessmentLimit',
                },
                {
                    type: 'string',
                    cssClass: `${this.cssClassLightBg} font-bold! md:text-right`,
                    value: formatCurrency(contributionAssessmentLimit, this.locale, '€'),
                },
            ]);
        }

        if (showMinimumAssessmentBasis) {
            rows.push([
                {
                    type: 'string',
                    cssClass: showContributionAssessmentLimit ? this.cssClassDarkBg : this.cssClassLightBg,
                    value: 'stContributionCalculator.results.minimumAssessmentBasis',
                },
                {
                    type: 'string',
                    cssClass: `${showContributionAssessmentLimit ? this.cssClassDarkBg : this.cssClassLightBg} font-bold! md:text-right`,
                    value: formatCurrency(minimumAssessmentBasis, this.locale, '€'),
                },
            ]);
        }

        return rows;
    });

    public ngOnChanges(changes: TypedSimpleChanges<ContributionCalculatorResultComponent>): void {
        if (changes.calculationResult?.currentValue && !changes.calculationResult.firstChange) {
            this.scrollIntoView();
        }
    }

    public ngAfterViewInit(): void {
        this.scrollIntoView();
    }

    private scrollIntoView(): void {
        const scrollElement = reflectComponentType(ContributionCalculatorResultComponent)?.selector;
        this.scrollService.scroll(scrollElement);
    }
}
